import { Alert, Button, Icon, LoadingOverlay, Pagination, SectionTitle, TextareaInput } from "@/components"
import { InputWithPlaceholders } from "@/components/InputWithPlaceholders/InputWithPlaceholders"
import {
    getMessageFieldName,
    getMessageVariationFieldName,
} from "@/pages/campaign-form/components/campaigns/writeALetter/Message.helper"
import { useStyles } from "@/pages/campaign-form/components/sections/message/MessageVariationSection/MessageVariationSection.styles"
import { filterContactPlaceholders, validatePlaceholders } from "@/pages/campaign-form/helpers"
import { CampaignFormValues } from "@/pages/campaign-form/types"
import { fetchMessageVariations, useCampaignPlaceholders } from "@/services/campaigns"
import React, { useMemo } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"
import { BadgesSection } from "@/pages/campaign-form/components/sections/message/MessageVariationSection/BadgesSection"

const { DraftOptions, MessageVariationsType, MessageVariationGenerationAiType } = DjangIO.app.grassroots.campaign.types

interface MessageVariationSectionProps {
    messageIndex: number
    currentMessageIndex: number
    setCurrentMessageIndex: React.Dispatch<React.SetStateAction<number>>
    setNumberOfVariations: React.Dispatch<React.SetStateAction<string>>
    showOverlay: boolean
    setOverlay: React.Dispatch<React.SetStateAction<boolean>>
    errorMessage: string
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>
    prefix?: string
}

export const MessageVariationSection = ({
    messageIndex,
    currentMessageIndex,
    setCurrentMessageIndex,
    setNumberOfVariations,
    showOverlay,
    setOverlay,
    errorMessage,
    setErrorMessage,
    prefix,
}: MessageVariationSectionProps) => {
    const name = `${prefix ? prefix + "." : ""}message_groups[${messageIndex}].message_variations`
    const { classes } = useStyles()
    const { control, ...formContext } = useFormContext<CampaignFormValues>()
    const { fields, remove, update } = useFieldArray({
        name,
        control,
    })
    const placeholders = useCampaignPlaceholders()
    const allPlaceholders = useMemo(() => {
        return placeholders.data?.reduce((acc, placeholder) => {
            return [...acc, ...placeholder.items]
        }, [])
    }, [placeholders])

    const contactPlaceholders = useMemo(() => {
        return filterContactPlaceholders(placeholders?.data)
    }, [placeholders])

    const onClickPagination = (page: number) => {
        setCurrentMessageIndex(page)
    }

    const archiveMessageVariation = (index: number) => {
        const variation = formContext.getValues(getMessageFieldName(messageIndex, "message_variations", prefix))[index]
        const messageGroup = formContext.getValues(`${prefix ? prefix + "." : ""}message_groups`)[messageIndex]

        const toJumpTo = index === 0 ? 0 : index - 1

        remove(index)
        setCurrentMessageIndex(toJumpTo + 1)

        const archivedVariations = [...(messageGroup.archived_variations || []), { ...variation, archived: true }]
        formContext.setValue(getMessageFieldName(messageIndex, "archived_variations", prefix), archivedVariations)
        setNumberOfVariations((prev: string) => String(Number(prev) - 1))
    }

    const generateUsingAI = async () => {
        const { subject, content, fixed_pre_content, fixed_post_content } = formContext.getValues(
            getMessageFieldName(messageIndex, "message_variations", prefix),
        )[0]
        const advocate_editing_permission = formContext.getValues(
            getMessageFieldName(messageIndex, "advocate_editing_permission", prefix),
        )

        try {
            setOverlay(true)
            setErrorMessage("")
            const [generatedVariation] = await fetchMessageVariations(
                subject,
                content,
                fixed_pre_content,
                fixed_post_content,
            )

            const current_message = formContext.getValues(
                getMessageFieldName(messageIndex, "message_variations", prefix),
            )[currentMessageIndex - 1]

            update(currentMessageIndex - 1, {
                ...current_message,
                ai_generation_type: MessageVariationGenerationAiType.ai_generated.value,
                subject: generatedVariation.subject,
                content: generatedVariation.content,
                ...(advocate_editing_permission === DraftOptions.partial_edit.value && {
                    fixed_pre_content: generatedVariation.fixed_pre_content,
                    fixed_post_content: generatedVariation.fixed_post_content,
                }),
            })
        } catch (e) {
            console.error(e)
            setErrorMessage("We had a problem loading this message; please try again.")
        } finally {
            setOverlay(false)
        }
    }

    const shouldRenderDeleteButton =
        formContext.watch(getMessageFieldName(messageIndex, "message_variations", prefix))?.length > 1

    const shouldRenderRegenerateButton =
        formContext.watch(getMessageFieldName(messageIndex, "message_variations_type", prefix)) ===
            MessageVariationsType.auto_generated.value && currentMessageIndex > 1

    const shouldRenderAnyButtons = shouldRenderDeleteButton || shouldRenderRegenerateButton

    const watchHasCustomEmailTargets = formContext.watch(
        getMessageFieldName(messageIndex, "has_custom_email_targets", prefix),
    )

    const addEditedBadge = () => {
        if (currentMessageIndex === 1) {
            return
        }

        formContext.setValue(
            getMessageVariationFieldName(messageIndex, currentMessageIndex - 1, "ai_generation_type", prefix),
            MessageVariationGenerationAiType.ai_generated_and_edited.value,
            { shouldValidate: true, shouldDirty: true },
        )
    }

    const isPartialEdit =
        formContext.watch(getMessageFieldName(messageIndex, "advocate_editing_permission", prefix)) ===
        DraftOptions.partial_edit.value

    const messageVariationField = formContext.getFieldState(
        getMessageFieldName(messageIndex, "message_variations", prefix),
    )

    return (
        <div className={classes.wrapper}>
            <LoadingOverlay
                visible={showOverlay}
                overlayBlur={4}
                overlayOpacity={0.5}
                radius={4}
                loaderProps={{
                    size: "xs",
                }}
            />
            <div className={classes.box}>
                <div className={classes.variationWrapper}>
                    {messageVariationField.invalid && (
                        <Alert
                            color="red"
                            radius="md"
                            withCloseButton
                            icon={<Icon icon="exclamation-triangle" />}
                            className={classes.variationValidationAlert}
                        >
                            Some of your message variations contain validation errors. Please correct them to proceed to
                            the next step.
                        </Alert>
                    )}
                    <div className={classes.titleWrapper}>
                        <SectionTitle
                            title={`Message to ${watchHasCustomEmailTargets ? "Custom Recipients" : "Officials"}`}
                        />
                        <BadgesSection
                            messageIndex={messageIndex}
                            currentMessageIndex={currentMessageIndex}
                            prefix={prefix}
                        />
                    </div>
                    <div>
                        {fields.map((_, index) => (
                            <div
                                // @ts-expect-error
                                key={`${fields[index]?.id}|${fields[index]?.index}`}
                                className={`${classes.variation} ${index === currentMessageIndex - 1 ? "" : classes.hiddenVariation}`}
                            >
                                <InputWithPlaceholders
                                    name={getMessageVariationFieldName(messageIndex, index, "subject", prefix)}
                                    rules={{
                                        required: "Subject is required.",
                                        validate: (variation) => validatePlaceholders(variation),
                                    }}
                                    label="Email subject"
                                    placeholder="Enter email subject"
                                    onKeyUp={addEditedBadge}
                                    placeholders={watchHasCustomEmailTargets ? contactPlaceholders : allPlaceholders}
                                />
                                {isPartialEdit && (
                                    <InputWithPlaceholders
                                        renderInput={(props) => (
                                            <TextareaInput
                                                {...props}
                                                label={props.label}
                                                name={props.name}
                                                rules={props.rules}
                                                triggerOnChange={false}
                                                onKeyUp={addEditedBadge}
                                            />
                                        )}
                                        label="Fixed pre-message"
                                        name={getMessageVariationFieldName(
                                            messageIndex,
                                            index,
                                            "fixed_pre_content",
                                            prefix,
                                        )}
                                        placeholders={
                                            watchHasCustomEmailTargets ? contactPlaceholders : allPlaceholders
                                        }
                                        rules={{
                                            validate: validatePlaceholders,
                                        }}
                                    />
                                )}
                                <InputWithPlaceholders
                                    label="Email body"
                                    name={getMessageVariationFieldName(messageIndex, index, "content", prefix)}
                                    renderInput={(props) => (
                                        <TextareaInput
                                            {...props}
                                            label={props.label}
                                            name={props.name}
                                            rules={props.rules}
                                            triggerOnChange={false}
                                            placeholder="Enter message"
                                            onKeyUp={addEditedBadge}
                                        />
                                    )}
                                    placeholders={watchHasCustomEmailTargets ? contactPlaceholders : allPlaceholders}
                                    rules={{
                                        required: !isPartialEdit ? "Email body cannot be empty." : false,
                                        validate: validatePlaceholders,
                                    }}
                                />
                                {isPartialEdit && (
                                    <InputWithPlaceholders
                                        renderInput={(props) => (
                                            <TextareaInput
                                                {...props}
                                                label={props.label}
                                                name={props.name}
                                                rules={props.rules}
                                                triggerOnChange={false}
                                                onKeyUp={addEditedBadge}
                                            />
                                        )}
                                        label="Fixed post-message"
                                        name={getMessageVariationFieldName(
                                            messageIndex,
                                            currentMessageIndex - 1,
                                            "fixed_post_content",
                                            prefix,
                                        )}
                                        placeholders={
                                            watchHasCustomEmailTargets ? contactPlaceholders : allPlaceholders
                                        }
                                        rules={{
                                            validate: validatePlaceholders,
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                {shouldRenderAnyButtons && (
                    <div className={classes.buttons}>
                        {shouldRenderRegenerateButton && (
                            <Button
                                onClick={async () => await generateUsingAI()}
                                variant="tonal"
                                leftIcon={<Icon icon="sync" />}
                                size="xs"
                            >
                                Regenerate Message
                            </Button>
                        )}
                        {shouldRenderDeleteButton && (
                            <Button
                                onClick={() => archiveMessageVariation(currentMessageIndex - 1)}
                                variant="outline"
                                leftIcon={<Icon icon="trash-alt" />}
                                size="xs"
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                )}
            </div>
            <div>
                <div className={classes.bottomWrapper}>
                    {errorMessage && (
                        <Alert icon={<Icon icon="exclamation-circle" iconFamily="fas" />} color="red">
                            {errorMessage}
                        </Alert>
                    )}
                    {formContext.watch(getMessageFieldName(messageIndex, "message_variations_type", prefix)) !==
                        MessageVariationsType.none.value && (
                        <Pagination
                            total={fields.length || 0}
                            onChange={onClickPagination}
                            value={currentMessageIndex}
                            size="sm"
                            withEdges
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
